import React, { useState, useEffect } from "react";
import {
  checkNoSpecialChars,
  checkSvg,
  removeSpecialChars,
  removeScriptsFromSvg,
} from "./system-icons";
import getSvgImgSrc from "../../core/utils/getSvgImgSrc";
import CompanySelector from "./company-selector";
import UnsavedChanges, { UnsavedChangesManual } from "../../partials/unsaved-changes";

function CompanyIcons(props) {
  const {
    newCompanyIconToggle,
    editingCompanyIcon,
    creatingNewCompanyIcon,
    onChangeIconCompany,
    assignedCompanies,
    setCompanyIconName,
    setCompanyIconSvg,
    saveNewCompanyIcon,
    companyIconToEdit,
    setCompanyIconToEditName,
    setCompanyIconToEditSvg,
    updateCompanyIconToEdit,
    backBtn,
    cancel,
    companyList,
    companyIconLists,
    editSvgIcon,
    deleteIcon,
    onSelectCompany = () => {},
  } = props;

  const [updatedName, setUpdatedName] = useState({
    value: "",
    finalValue: "",
  });
  const [nameError, setNameError] = useState("");
  const [updatedSvg, setUpdatedSvg] = useState("");
  const [editingSvg, setEditingSvg] = useState("");
  const [svgError, setSvgError] = useState("");
  const [companyValue, setCompanyValue] = useState("");

  const [createNewDisabled, setCreateNewDisabled] = useState(true);
  const [updateDisabled, setUpdateDisabled] = useState(true);
  const [originalInfo, setOriginalInfo] = useState();
  const [showManualModel, setShowManualModel] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const onCreateNewName = (v) => {
    if (checkNoSpecialChars(v.target.value)) {
      setNameError("");
      setUpdatedName({ value: v.target.value, finalValue: v });
    } else {
      setNameError("*Special characters are not allowed.");
    }
  };

  const onCreateNewSvg = (s) => {
    if (s.target.value.length > 0) {
      const cleanedSvg = removeScriptsFromSvg(s.target.value);
      const isValidSvg = checkSvg(cleanedSvg);
      if (isValidSvg) {
        setSvgError("");
        setUpdatedSvg(cleanedSvg);
        setCompanyIconSvg({ target: { value: cleanedSvg } });
      } else {
        setSvgError(
          "*Please check the SVG code and make sure it is in a valid format."
        );
      }
    } else {
      setSvgError("");
    }
  };

  const onSelectNewCompany = (c) => {
    setCompanyValue(c.target.value);
    onChangeIconCompany(c);
  };

  const onSaveNewName = () => {
    if (updatedName.finalValue) {
      setCompanyIconName(updatedName.finalValue);
    }
  };

  const onSubmitNew = () => {
    saveNewCompanyIcon();
    onCancel();
  };

  const onCancel = () => {
    setNameError("");
    setUpdatedName({ value: "", finalValue: "" });
    setUpdatedSvg("");
    setSvgError("");
    setCreateNewDisabled(true);
    setUpdateDisabled(true);
    setCompanyValue("");
    cancel();
  };

  useEffect(() => {
    if (
      updatedName.value.length < 1 ||
      updatedSvg === "" ||
      companyValue === ""
    ) {
      setCreateNewDisabled(true);
    } else {
      setCreateNewDisabled(false);
    }
  }, [updatedName, updatedSvg, companyValue]);

  /**
   * Editing functions
   */

  const onEditName = (v) => {
    if (checkNoSpecialChars(v.target.value)) {
      setNameError("");
      setCompanyIconToEditName(v);
    } else {
      setNameError("*Special characters are not allowed.");
      v.target.value = removeSpecialChars(v.target.value);
      setCompanyIconToEditName(v);
    }
  };

  const onEditSvg = (s) => {
    if (s.target.value.length > 0) {
      const cleanedSvg = removeScriptsFromSvg(s.target.value);
      const isValidSvg = checkSvg(cleanedSvg);
      if (isValidSvg) {
        setSvgError("");
        setEditingSvg(cleanedSvg);
        setCompanyIconToEditSvg({target: {value: cleanedSvg}});
      } else {
        setEditingSvg(s.target.value);
        setCompanyIconToEditSvg(s);
        setSvgError(
          "*Please check the SVG code and make sure it is in a valid format."
        );
      }
    } else {
      setSvgError("");
      setEditingSvg(s.target.value);
      setCompanyIconToEditSvg(s);
    }
  };

  const onSubmitEdit = () => {
    updateCompanyIconToEdit();
    onCancel();
  };

  useEffect(() => {
    if (companyIconToEdit.name === "" || companyIconToEdit.svg === "") {
      setUpdateDisabled(true);
    } else {
      setUpdateDisabled(false);
    }
  }, [companyIconToEdit]);

  const onBack = () => {
    onCancel();
    backBtn();
  };

  /**
   * scroll to show editor section when clicking an item down the left side list
   */
  const scrollToEditor = React.useRef(null);
  useEffect(() => {
    let didCancel = false;
    if (!didCancel) {
      if (Object.keys(companyIconToEdit).length > 0 && editingCompanyIcon) {
        scrollToEditor.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
    return () => {
      didCancel = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIconToEdit]);

  useEffect(() => {
    let didCancel = false;
    if(!didCancel) {
      setEditingSvg(companyIconToEdit.svg)
    }
    return () => {
      setEditingSvg("")
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIconToEdit.id]);

  useEffect(() => {
    if(creatingNewCompanyIcon) {
      setUpdatedName({ value: "", finalValue: "" });
      setUpdatedSvg("");
    }
    if(editingCompanyIcon) {
      setEditingSvg(companyIconToEdit.svg);
      setUpdateDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingNewCompanyIcon, companyIconToEdit.svg])

  useEffect(() => {
    setOriginalInfo(companyIconToEdit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIconToEdit.id])

  useEffect(() => {
    let didCancel = false;
    const disableUpdate = () => {
      if (!companyIconToEdit.name.length || !editingSvg.length) {
        setUpdateDisabled(true); // check fields are not empty
        return;
      } 
      if (originalInfo.name === companyIconToEdit.name && originalInfo.svg === editingSvg) {
        setUpdateDisabled(true); // check if values have changed
        return;
      } 
      if (svgError.length) {
        setUpdateDisabled(true); // check it has errors
        return;
      }
      setUpdateDisabled(false);
    };
    if (Object.keys(companyIconToEdit).length && Object.keys(originalInfo).length && !didCancel) {
      disableUpdate();
    }
    return () => {
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIconToEdit, originalInfo]);

  const checkBeforeLeave = () => {
    if(hasUnsavedChanges) {
      setShowManualModel(true);
    } else {
      onBack();
    }
  }

  const handleUnsavedLeave = () => {
    setShowManualModel(false);
    onBack();
  }

  const handleUnsavedCancel = () => {
    setShowManualModel(false);
  }

  useEffect(() => {
    if(!updateDisabled && editingCompanyIcon) {
      setHasUnsavedChanges(true);
    } else if(!createNewDisabled){
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createNewDisabled, updateDisabled])

  return (
    <>
      <div className="flex-inline mb-5">
        <button type="button" className="btn-back-icons" onClick={checkBeforeLeave}>
          <i className="bi bi-chevron-left"></i> Back
        </button>
        <h3>Company Icons</h3>
      </div>

      <div className="icon-list-section">
        <div className="left-section-wrapper">
          <CompanySelector
            list={companyList}
            locationType={companyIconLists}
            deleteLocationType={deleteIcon}
            editLocationType={editSvgIcon}
            svgIcons
            hasChanged={() => {}}
            onCompanySelect={onSelectCompany}
          />
        </div>

        <div className="right-panel">
          {!editingCompanyIcon && (
            <button
              onClick={newCompanyIconToggle}
              disabled={creatingNewCompanyIcon}
            >
              New Company Icon
            </button>
          )}

          {creatingNewCompanyIcon && !editingCompanyIcon && (
            <div className="create-new-section new-system-icon-sm">
              <br />
              <div>
                <span className="label">Company:</span>
                <select onChange={onSelectNewCompany}>
                  <option />
                  {assignedCompanies.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {companyValue && <>

                <div>
                  <span className="label">Name:</span>
                  <input
                    type="text"
                    value={updatedName.value}
                    onChange={(e) => onCreateNewName(e)}
                    onBlur={onSaveNewName}
                  />
                  <span className="error-msg">{nameError}</span>
                </div>

                <div>
                  <span className="label">Icon Preview:</span>
                  {updatedSvg && (
                    <>
                      <img
                        className="icon-preview"
                        height="40"
                        width="40"
                        src={getSvgImgSrc(updatedSvg)}
                        alt="icon"
                      />
                      <span className="icon-preview-note">
                        <u>Note</u>: The preview displays the icon as it will show
                        once created. Please make sure it displays correctly here.
                      </span>
                    </>
                  )}
                </div>

                <div>
                  <span className="label">SVG:</span>
                  <textarea
                    placeholder="Place SVG code here."
                    onChange={onCreateNewSvg}
                  />
                  <span className="error-msg">{svgError}</span>
                </div>
              </>}

              <div className="edit-btn-section">
                <button onClick={onCancel}>Cancel</button>
                <button className="promote" onClick={onSubmitNew} disabled={createNewDisabled}>
                  Save
                </button>
              </div>

            </div>
          )}

          {editingCompanyIcon && (
            <EditCompanyIcon 
              scrollToEditor={scrollToEditor}
              companyIconToEdit={companyIconToEdit}
              onEditName={onEditName}
              nameError={nameError}
              editingSvg={editingSvg}
              onEditSvg={onEditSvg}
              svgError={svgError}
              onCancel={onCancel}
              onSubmitEdit={onSubmitEdit}
              updateDisabled={updateDisabled}
            />
          )}
        </div>
      </div>
      <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
      <UnsavedChangesManual showModal={showManualModel} confirmLeave={handleUnsavedLeave} cancelLeave={handleUnsavedCancel} />
    </>
  );
}

export default CompanyIcons;


/*
* edit company icon component
*/
const EditCompanyIcon = (props) => {
  const {
    scrollToEditor,
    companyIconToEdit,
    onEditName,
    nameError,
    editingSvg,
    onEditSvg,
    svgError,
    onCancel,
    onSubmitEdit,
    updateDisabled
  } = props;

  return (
    <div className="create-new-section new-system-icon-sm"> 
      <div className="scroll-stopper" ref={scrollToEditor}></div>
      <div className="editor-section">
        <span className="label">Edit Name:</span>
        <input
          type="text"
          value={companyIconToEdit.name}
          onChange={onEditName}
        />
        <span className="error-msg">{nameError}</span>
      </div>

      <div>
        <span className="label">Icon Preview:</span>
        {editingSvg && (
          <>
            <img
              className="icon-preview"
              height="40"
              width="40"
              src={getSvgImgSrc(editingSvg)}
              alt="icon"
            />
            <span className="icon-preview-note">
              <u>Note</u>: The preview displays the icon as it will show
              once created. Please make sure it displays correctly here.
            </span>
          </>
        )}
      </div>
      <div>
        <span className="label">SVG:</span>
        <textarea
          placeholder="Paste SVG code here."
          value={editingSvg}
          onChange={onEditSvg}
        />
        <span className="error-msg">{svgError}</span>
      </div>
      <div className="edit-btn-section">
        <button onClick={onCancel}>Cancel</button>
        <button className="promote" onClick={onSubmitEdit} disabled={updateDisabled}>
          Update
        </button>
      </div>
    </div>
  )
}